import type {
  NonPaginatedSelectDataSource,
  NonPaginatedSelectDataSourceOptions,
  PaginatedSelectDataSource,
  PaginatedSelectDataSourceOptions,
  SelectDataSource,
  SelectLoadRequest,
  UseSelectDataSourceOptions,
} from './SelectDataSource.types';

import { useQueryClient, type DefaultError } from '@tanstack/react-query';
import { useMemo } from 'react';

export type {
  NonPaginatedSelectDataSource,
  PaginatedSelectDataSource,
  SelectDataSource,
  SelectItemQueryOptions,
  SelectLoadRequest,
  SelectPage,
  UseSelectDataSourceOptions,
} from './SelectDataSource.types';

function getDefaultItems<TItem, TResponse>(response: TResponse) {
  return response as TItem[];
}

function getDefaultOptionLabel<TItem>(item: TItem) {
  return (item as { label: string }).label;
}

function getDefaultOptionValue<TItem>(item: TItem) {
  return (item as { value: string }).value;
}

/**
 * Adapts TanStack Query factories and API response shapes to the stable
 * data-source contract consumed by async select controls.
 *
 * List queries load searchable options through QueryClient so identical
 * requests share TanStack's cache. Optional item queries are forwarded to the
 * control, which uses them to resolve initial or programmatically assigned
 * values that are not present in its currently known options.
 */
export function useSelectDataSource<
  TResponse,
  TItem,
  TPageParam = number,
  TError = DefaultError,
  TFilters = undefined,
>(
  options: NonPaginatedSelectDataSourceOptions<
    TResponse,
    TItem,
    TPageParam,
    TError,
    TFilters
  >,
): NonPaginatedSelectDataSource<TItem, TPageParam, TFilters>;
export function useSelectDataSource<
  TResponse,
  TItem,
  TPageParam = number,
  TError = DefaultError,
  TFilters = undefined,
>(
  options: PaginatedSelectDataSourceOptions<
    TResponse,
    TItem,
    TPageParam,
    TError,
    TFilters
  >,
): PaginatedSelectDataSource<TItem, TPageParam, TFilters>;

export function useSelectDataSource<
  TResponse,
  TItem,
  TPageParam = number,
  TError = DefaultError,
  TFilters = undefined,
>({
  getItems = getDefaultItems,
  getNextPageParam,
  getOptionLabel = getDefaultOptionLabel,
  getOptionValue = getDefaultOptionValue,
  initialPageParam,
  itemQueryOptions,
  isPaginated = false,
  listQueryKey,
  listQueryOptions,
}: UseSelectDataSourceOptions<
  TResponse,
  TItem,
  TPageParam,
  TError,
  TFilters
>): SelectDataSource<TItem, TPageParam, TFilters> {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const findCachedItem = listQueryKey
      ? (value: string) => {
          const cachedLists = queryClient.getQueriesData<TResponse>({
            queryKey: listQueryKey,
          });

          for (const [, response] of cachedLists) {
            if (response === undefined) {
              continue;
            }

            let items: TItem[];
            try {
              items = getItems(response);
            } catch {
              // A query-key family can also contain infinite or summary
              // responses that intentionally use another response shape.
              continue;
            }
            if (!Array.isArray(items)) {
              continue;
            }

            const item = items.find(
              (candidate) => getOptionValue(candidate) === value,
            );
            if (item) {
              return item;
            }
          }

          return undefined;
        }
      : undefined;
    const loadOptions = async ({
      filters,
      pageParam = initialPageParam,
      search,
    }: SelectLoadRequest<TPageParam, TFilters>) => {
      const queryOptions = listQueryOptions({
        filters,
        pageParam,
        search,
      });
      const response = await queryClient.fetchQuery(queryOptions);

      return {
        items: getItems(response),
        nextPageParam:
          pageParam === undefined || !getNextPageParam
            ? undefined
            : getNextPageParam(response, pageParam),
      };
    };
    const commonDataSource = {
      findCachedItem,
      getOptionLabel,
      getOptionValue,
      itemQueryOptions,
      loadOptions,
    };

    return isPaginated
      ? {
          ...commonDataSource,
          initialPageParam: initialPageParam as TPageParam,
          isPaginated: true as const,
        }
      : {
          ...commonDataSource,
          initialPageParam,
          isPaginated: false as const,
        };
  }, [
    getItems,
    getNextPageParam,
    getOptionLabel,
    getOptionValue,
    initialPageParam,
    itemQueryOptions,
    isPaginated,
    listQueryKey,
    listQueryOptions,
    queryClient,
  ]);
}
